<script lang="ts">
    import { openModal } from 'svelte-modals';
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';

    import { debug, deliveryList, planning, scenarioManager } from '../../../../stores/store';
    import IconPlanningStep3 from '../../../shared/Icons/IconPlanningStep3.svelte';
    import ModalSelectRoute from '../../Modals/ModalSelectRoute.svelte';
    import ModalSetTargetDate from '../../Modals/ModalSetTargetDate.svelte';
    import { DeliveriesBulkActions } from './DeliveriesBulkActions';
    import ModalConfirm from '../../../shared/Modals/ModalConfirm.svelte';
    import IconVehicle from '../../../shared/Icons/IconVehicle.svelte';
    import IconRemove from '../../../shared/Icons/IconRemove.svelte';
    import IconClose from '../../../shared/Icons/IconClose.svelte';
    import IconCalendar from '../../../shared/Icons/IconCalendar.svelte';
    import Spinner from '../../../shared/Spinner/Spinner.svelte';
    import { api } from '../../../../stores/api';
    import ModalError from '../../../shared/Modals/ModalError.svelte';
    import { dla } from './DeliveryListApp/dla';
    import { DeliveriesMenuActions } from './DeliveriesMenuActions';
    import { BulkAction } from '../BulkAction';
    import BulkActionOne from '../BulkActionOne.svelte';
    import type { AssignmentJob } from '../AssignmentJob';
    import { JobApi } from '../../Jobs/JobApi';
    import { jobs } from '../../../../stores/store';
    import { features } from '../../../shared/Features/features';
    import { Umami } from '../../../utilities/Umami';

    export let disabled: boolean = false;

    let selectedItems: any[] = [];
    let selectedCount: number = 0;
    let jobsToConfirm: number[] = [];
    let jobsToReject: number[] = [];
    let jobsToRemoveFromRoute: number[] = [];
    let jobsToAssignVehicle: number[] = [];
    let inProgress: boolean = false;

    let actions: Record<string, BulkAction> = {
        'assign': new BulkAction(
            IconPlanningStep3,
            $_('Accept'),
            (items: any[]) => {
                setAssign(items);
            },
            true
        ),
        'reject': new BulkAction(
            IconRemove,
            $_('Reject'),
            (items: any[]) => {
                removeAssign(items);
            },
            true
        ),
        'removeFromRoute': new BulkAction(
            IconRemove,
            $_('Remove from route'),
            (items: any[]) => {
                removeAssign(items);
            },
            true
        ),
        'selectVehicle': new BulkAction(
            IconVehicle,
            $_('Select vehicle'),
            (items: any[]) => {
                selectVehicle(items);
            },
            true
        )
    }
    if ($features.feature_ux_improvements) {
        actions.changeTargetDate = new BulkAction(
            IconCalendar,
            $_('change_target_date'),
            (items: any[]) => {
                changeTargetDate(items);
            },
            true
        );
    }

    function isJobAvailableForAction(action: string, aj: AssignmentJob|null): boolean {
        if (aj) {
            switch (action) {
                case 'assign':
                    return DeliveriesMenuActions.deliveryCanBeAssigned(aj, $dla.getAssignment());
                case 'reject':
                    return DeliveriesMenuActions.deliveryCanBeRejected(aj, $dla.getAssignment());
                case 'removeFromRoute':
                    return DeliveriesMenuActions.hasAssignment(aj);
                case 'selectVehicle':
                    return DeliveriesMenuActions.deliveryCanBeAssignedToAnotherVehicle(aj, $dla.getAssignment());
                case 'changeTargetDate':
                    return DeliveriesMenuActions.deliveryCanBeAssignedToAnotherVehicle(aj, $dla.getAssignment());
            }
        }
        return false;
    }

    function doAssign(assignment_id: number, jobsToProcess: number[]) {
        if (disabled) {
            console.warn('disabled, cannot doAssign');
            return;
        }
        inProgress = true;
        $dla.getAssignment().setBusy(true);
        DeliveriesBulkActions.doAssign(
            $api,
            assignment_id,
            jobsToProcess,
            'selected',
            {},
            $planning.selectedDay
        )
            .then(_ => {
                Umami.event('job_manual_assign', { count: jobsToProcess.length, assignment_id });
                $dla.resetStatus(true);
                $deliveryList.selectedOrUnselected = [];
                $deliveryList.selectMode = 'selected';
                $deliveryList.triggerReload = true;
            })
            .catch(err => {
                Umami.error('job_manual_assign', err, { count: jobsToProcess.length, assignment_id });
                console.error('err', err);
                openModal(ModalError, {
                    error: err,
                    header: '',
                    message: $_(err),
                });
            })
            .finally(() => {
                inProgress = false;
                try {
                    $dla.getAssignment().setBusy(false);
                } catch (e) {
                    console.warn('assignment is not set, so cannot set busy false');
                }
            });
    }

    function setAssign(jobsToProcess: number[]) {
        if (disabled) {
            console.warn('disabled, cannot setAssign');
            return;
        }
        if ($deliveryList.currentAssignment === null) {
            console.warn('no assignment selected');
            return;
        }
        inProgress = true;
        DeliveriesBulkActions.doAssign(
            $api,
            $deliveryList.currentAssignment.getId(),
            jobsToProcess,
            'selected',
            {},
            $planning.selectedDay
        )
            .then(_ => {
                Umami.event('manual_job_confirm', { count: jobsToProcess.length, assignment_id: $deliveryList.currentAssignment?.getId() });
                $dla.resetStatus(true);
                $deliveryList.selectedOrUnselected = [];
                $deliveryList.selectMode = 'selected';
                $deliveryList.triggerReload = true;
            })
            .catch(err => {
                Umami.error('manual_job_confirm', err, { count: jobsToProcess.length });
                console.error('err', err);
                openModal(ModalError, {
                    error: err,
                    header: '',
                    message: $_(err),
                });
            })
            .finally(() => {
                inProgress = false;
            });
    }

    function removeAssign(jobsToProcess: number[]) {
        if (disabled) {
            console.warn('disabled, cannot removeAssign');
            return;
        }
        if ($deliveryList.currentAssignment === null) {
            console.warn('no assignment selected');
            return;
        }
        inProgress = true;
        DeliveriesBulkActions.doReject(
            $api,
            jobsToProcess,
            'selected',
            {},
            $deliveryList.currentAssignment?.getId(),
            $planning.selectedDay
        )
            .then(_ => {
                Umami.event('job_operator_reject', { count: jobsToProcess.length, assignment_id: $deliveryList.currentAssignment?.getId() });
                $dla.resetStatus(true);
                $deliveryList.selectedOrUnselected = [];
                $deliveryList.selectMode = 'selected';
                $deliveryList.triggerReload = true;
            })
            .catch(err => {
                Umami.error('job_operator_reject', err, { count: jobsToProcess.length });
                console.error('err', err);
                openModal(ModalError, {
                    error: err,
                    header: '',
                    message: $_(err),
                });
            })
            .finally(() => {
                inProgress = false;
            });
    }

    function selectVehicle(jobsToProcess: number[]) {
        if (disabled) {
            console.warn('disabled, cannot selectVehicle');
            return;
        }
        openModal(ModalSelectRoute, {
            onClick: (assignmentId, assignment) => {
                return new Promise((resolve, reject) => {
                    if (assignmentId !== null) {
                        let assignment = $scenarioManager.getAssignmentById(assignmentId);
                        if (assignment?.isRouteCalculated()) {
                            let confirm: { title: string; message: string };
                            if (assignment.isLocked()) {
                                if (assignment.isDownloaded()) {
                                    confirm = {
                                        title: $_('route_already_downloaded'),
                                        message: $_(
                                            'new_jobs_will_be_added_on_commit_subject_to_approval'
                                        ),
                                    };
                                } else {
                                    confirm = {
                                        title: $_('route_already_locked'),
                                        message: $_('adding_jobs_unlocks_route'),
                                    };
                                }
                            } else {
                                confirm = {
                                    title: $_('assignment_has_route_calculated'),
                                    message: $_('assignment_route_will_be_removed_on_assign'),
                                };
                            }
                            openModal(ModalConfirm, {
                                title: confirm.title,
                                text: confirm.message,
                                onClick: () => {
                                    doAssign(assignmentId, jobsToProcess);
                                    resolve(true);
                                },
                                onCancel: () => {
                                    resolve(false);
                                },
                            });
                            return;
                        } else {
                            doAssign(assignmentId, jobsToProcess);
                            resolve(true);
                        }
                    } else {
                        resolve(true);
                    }
                });
            },
            selectedScenarioId: $scenarioManager.getScenarioId(),
            vehicles: $scenarioManager.getVehiclesData(),
            inProgressSwitch: false,
            onlyLocked: false,
        });
    }

    function changeTargetDate(jobsToProcess: number[]) {
        if (disabled) {
            console.warn('disabled, cannot removeAssign');
            return;
        }
        if ($deliveryList.currentAssignment === null) {
            console.warn('no assignment selected');
            return;
        }
        openModal(ModalSetTargetDate, {
            text: $_(`Select target date for selected jobs`) + ` (${selectedCount})`,
            onClick: () => {
                inProgress = true;
                DeliveriesBulkActions.doReject(
                    $api,
                    jobsToProcess,
                    'selected',
                    {},
                    null,
                    $planning.selectedDay
                )
                    .then(_ => {
                        return JobApi.modify(
                            get(api),
                            { id: { $in: jobsToProcess } },
                            { target_date: $jobs.defaultTargetDate }
                        )
                    })
                        .then(_ => {
                            $dla.resetStatus(true);
                            $deliveryList.selectedOrUnselected = [];
                            $deliveryList.selectMode = 'selected';
                            $deliveryList.triggerReload = true;
                        })
                        .catch(err => {
                            console.error('err', err);
                            openModal(ModalError, {
                                error: err,
                                header: '',
                                message: $_(err),
                            });
                        })
                        .finally(() => {
                            inProgress = false;
                        });
            }
        });
    }

    $: {

        try {
            $deliveryList.displayedAssignmentJobDetails = $dla.getFilteredItems().map(aj => {
                return {
                    jobId: aj.getJobId(),
                    hasAssignment: DeliveriesMenuActions.hasAssignment(aj),
                    hasSuggestion: aj.getSuggestedAssignmentId() === $dla.getAssignment().getId(),
                    hasAdhoc: aj.isAdHoc(),
                    canBeRejected: DeliveriesMenuActions.deliveryCanBeRejected(
                        aj,
                        $dla.getAssignment()
                    ),

                    canBeAssigned: DeliveriesMenuActions.deliveryCanBeAssigned(
                        aj,
                        $dla.getAssignment()
                    ),
                };
            });
        } catch (e) {
            console.warn(
                '$deliveryList.displayedAssignmentJobDetails set [] bcs $dla.getAssignment is undefined'
            );
            $deliveryList.displayedAssignmentJobDetails = [];
        }

        selectedItems = $deliveryList.displayedAssignmentJobDetails.filter(
            details =>
                ($deliveryList.selectMode === 'filtered' &&
                    !$deliveryList.selectedOrUnselected.includes(details.jobId)) ||
                ($deliveryList.selectMode === 'selected' &&
                    $deliveryList.selectedOrUnselected.includes(details.jobId))
        );

        console.log(
            'onlySelectedFields, total, ',
            $deliveryList.displayedAssignmentJobDetails.length
        );
        console.log('onlySelectedFields, $dla total, ', $dla.getFilteredItems().length);
        console.log('onlySelectedFields AA', selectedItems.length);

        selectedItems = selectedItems.filter(details => {
            let aj = $dla.getAssignmentJobByJobId(details.jobId);
            try {
                return aj && DeliveriesMenuActions.deliveryCanBeChecked(aj, $dla.getAssignment());
            } catch (e) {
                return false;
            }
        });

        console.log('onlySelectedFields BB', selectedItems.length);

        selectedCount = selectedItems.length;

        for (let a in actions) {
            actions[a] = actions[a]
                .setItems(
                    selectedItems.filter(details => {
                        let aj = $dla.getAssignmentJobByJobId(details.jobId);
                        try {
                            return isJobAvailableForAction(a, aj);
                        } catch (e) {
                            return false;
                        }
                    }).map(details => details.jobId)
                )
                .setVisibility(
                    a == 'selectVehicle' || $deliveryList.currentAssignment?.isDefault() || false
                );
        }

    }
</script>

<div class="lm--action_bar" style="display: flex;">
    {#if inProgress}
        <Spinner />
    {:else}
        <div>
            <span class="text-span-10">{$_('Selected items')}</span>:
            <strong>{selectedCount}</strong>
        </div>
        <div class="lm--action_bar_loptions" class:disabled>
            {#if $deliveryList.currentAssignment !== null}
                {#each Object.values(actions) as action}
                    <BulkActionOne {action}/>
                {/each}
            {/if}
        </div>
        <IconClose
            scale={0.7}
            on:click={() => {
                $deliveryList.selectedOrUnselected = [];
                $deliveryList.selectMode = 'selected';
            }}
        />
    {/if}
</div>

<style>
    .lm--action_bar {
        font-size: 14px;
        z-index: 1;
        min-height: 72px;
        color: #fff;
        background-color: #397be5;
        justify-content: space-between;
        align-items: center;
        padding: 10px 30px;
        display: none;
        position: absolute;
        column-gap: 20px;
        top: 0;
        bottom: auto;
        left: 0;
        right: 0;
    }
    .lm--action_bar_loptions {
        grid-column-gap: 30px;
        grid-row-gap: 20px;
        /*filter: invert();*/
        color: #000;
        align-items: center;
        font-weight: 500;
        line-height: 14px;
        display: flex;
        flex-wrap: wrap;
    }
    .disabled {
        opacity: 0.5;
        pointer-events: none;
    }
</style>
